import { ROOT_ADMIN_SK, YANGON_REGION_CODE } from 'src/lib/constants/stringConstants';

/**
 * Central frontend permission check.
 * Backend remains the security authority; this is UX-only.
 *
 * @param {Array<{name: string}>|null|undefined} userPermissions
 * @param {string|string[]|null|undefined} requiredPermission
 * @param {boolean} [isRootAdmin]
 * @returns {boolean}
 */
export const hasPermission = (userPermissions, requiredPermission, isRootAdmin = false) => {
  if (isRootAdmin) return true;
  if (requiredPermission == null || requiredPermission === '') return false;
  if (!Array.isArray(userPermissions) || userPermissions.length === 0) return false;

  if (Array.isArray(requiredPermission)) {
    return requiredPermission.some((perm) =>
      userPermissions.some((p) => p?.name === perm),
    );
  }

  return userPermissions.some((p) => p?.name === requiredPermission);
};

/**
 * Root Admin detection from API user.role string (UserResource).
 */
export const isRootAdminUser = (user) => user?.role === ROOT_ADMIN_SK;

/**
 * Yangon (MMR012) or Root Admin UI gate.
 * Used for product-master mutations and production-order CRUD.
 * Warehouse-only users count when an assigned warehouse is in Yangon.
 *
 * Backend APIs enforce the same rule.
 *
 * @param {object|null|undefined} user Authenticated user from UserResource
 * @returns {boolean}
 */
export const hasYangonRegionAccess = (user) => {
  if (!user) return false;
  if (isRootAdminUser(user)) return true;

  const regions = Array.isArray(user.regions) ? user.regions : [];
  if (regions.some((region) => region?.code === YANGON_REGION_CODE)) {
    return true;
  }

  const warehouses = Array.isArray(user.warehouses) ? user.warehouses : [];
  return warehouses.some((warehouse) => warehouse?.region?.code === YANGON_REGION_CODE);
};

/**
 * Product module mutation UI gate (Yangon or Root Admin).
 * List/detail remain permission-only; create/edit/delete/price-change
 * also require Yangon region (code MMR012) or Root Admin.
 */
export const canManageProductActions = hasYangonRegionAccess;
